import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from '@/lib/api';
import { toast } from "@/lib/toast-with-sound";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { YoutubeVideoManager } from "@/components/YoutubeVideoManager";
import { 
  Users, MapPin, Shield,
  Check, X, Clock, LogOut, UserPlus, Calendar,
  Loader2, Route, Gauge, ChevronRight, ChevronLeft,
  Globe, Lock, UsersRound, ArrowLeft, Mail, Camera, Star, Youtube, Share2, Pencil, Eye, Bike, Crown, ZoomIn, FileEdit
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { TripCard } from "@/components/TripCard";
import { ClubEventsPublicList } from "@/components/ClubEventsPublicList";
import { ShareModal } from "@/components/ShareModal";
import { useAuth } from "@/contexts/AuthContext";
import { useActionBar } from "@/contexts/ActionBarContext";
import { useTranslation } from "react-i18next";
import { getTranslatedValue } from "@/components/TranslatableField";
import { SEO } from "@/components/SEO";
import { getUploadUrl, quoteNickname } from "@/lib/utils";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { useJoinClubRequest, useLeaveClub, useApproveMember, useRejectMember } from "@/hooks/mutations";
import { useClub, useClubGallery, useClubVideos } from "@/hooks/queries";
import { BACKEND_URL } from "../config";
/**
 * GroupPhotoGalleryViewer - View-only photo gallery for clubs
 * Shows main photo indicator and allows navigating through photos in lightbox
 */
function GroupPhotoGalleryViewer({ mainPhoto, gallery, t }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Build combined list of all photos with main photo first
  const allPhotos = [];
  if (mainPhoto) {
    allPhotos.push({ filename: mainPhoto, is_main: true });
  }
  gallery.forEach(photo => {
    // Avoid duplicates if main photo is also in gallery
    if (photo.filename !== mainPhoto) {
      allPhotos.push({ ...photo, is_main: photo.is_main || false });
    }
  });
  
  const totalPhotos = allPhotos.length;
  
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  
  const navigateLightbox = (direction) => {
    if (direction === "prev") {
      setLightboxIndex(i => (i > 0 ? i - 1 : totalPhotos - 1));
    } else {
      setLightboxIndex(i => (i < totalPhotos - 1 ? i + 1 : 0));
    }
  };
  
  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") navigateLightbox("prev");
      if (e.key === "ArrowRight") navigateLightbox("next");
      if (e.key === "Escape") setLightboxOpen(false);
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, totalPhotos]);
  
  if (totalPhotos === 0) return null;
  
  const remainingPhotos = allPhotos.slice(1);
  
  return (
    <Card className="bg-card/80 backdrop-blur-sm border-border overflow-hidden">
      {/* Main photo — large, like TripSidebar */}
      {mainPhoto && (
        <div 
          className="relative w-full group cursor-pointer"
          onClick={() => openLightbox(0)}
        >
          <img 
            loading="lazy" 
            src={getUploadUrl(mainPhoto)}
            alt={t("group_detail.main_photo")}
            className="w-full h-auto group-hover:brightness-75 transition-all duration-300"
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/60 hover:bg-black/80 text-white text-sm font-medium backdrop-blur-sm transition-colors"
            >
              <ZoomIn className="h-4 w-4" />
              Zoom
            </button>
          </div>
          <div className="absolute top-2 left-2 p-1.5 bg-primary text-primary-foreground rounded-full shadow-md">
            <Star className="h-3.5 w-3.5 fill-current" />
          </div>
        </div>
      )}

      {/* Gallery thumbnails */}
      {remainingPhotos.length > 0 && (
        <>
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Camera className="h-5 w-5 text-primary" />
              {t("group_detail.photos")}
              <span className="text-sm text-muted-foreground">({totalPhotos})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {remainingPhotos.map((photo, index) => (
                <div 
                  key={photo.filename}
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity relative group"
                  onClick={() => openLightbox(index + 1)}
                >
                  <img loading="lazy" 
                    src={getUploadUrl(photo.filename)}
                    alt={`${t("group_detail.photo")} ${index + 2}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </>
      )}

      {/* If only main photo, show minimal label */}
      {remainingPhotos.length === 0 && mainPhoto && (
        <CardHeader className="pb-3 pt-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Camera className="h-5 w-5 text-primary" />
            {t("group_detail.photos")}
            <span className="text-sm text-muted-foreground">(1)</span>
          </CardTitle>
        </CardHeader>
      )}

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl bg-black/95 border-none p-0 sm:rounded-lg">
          <DialogHeader className="sr-only">
            <DialogTitle>{t("group_detail.view_photo")}</DialogTitle>
            <DialogDescription>{t("group_detail.photo_fullsize")}</DialogDescription>
          </DialogHeader>
          <div className="relative flex items-center justify-center min-h-[50vh]">
            {allPhotos[lightboxIndex] && (
              <img loading="lazy" 
                src={getUploadUrl(allPhotos[lightboxIndex].filename)}
                alt={t("group_detail.enlarged_photo")}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
            
            {/* Close button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors z-10"
              aria-label={t("close")}
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Navigation buttons */}
            {totalPhotos > 1 && (
              <>
                <button
                  onClick={() => navigateLightbox("prev")}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                  aria-label={t("group_detail.previous_photo")}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() => navigateLightbox("next")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                  aria-label={t("group_detail.next_photo")}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
            
            {/* Photo counter, caption, and main indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
              {allPhotos[lightboxIndex]?.caption && (
                <p className="text-white text-sm px-4 py-1 bg-black/60 rounded-xl max-w-md text-center">
                  {allPhotos[lightboxIndex].caption}
                </p>
              )}
              <div className="flex items-center gap-2">
                <div className="px-3 py-1.5 bg-black/60 text-white text-sm rounded-full">
                  {lightboxIndex + 1} / {totalPhotos}
                </div>
                {allPhotos[lightboxIndex]?.is_main && (
                  <div className="px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" />
                    {t("group_detail.main")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Visibility config - labels and tooltips are translated dynamically
const VISIBILITY_CONFIG = {
  draft: {
    labelKey: "group_detail.visibility.draft",
    icon: FileEdit,
    color: "text-amber-500 bg-amber-500/10 border-amber-500/50",
    tooltipKey: "group_detail.visibility.draft_tooltip"
  },
  public: { 
    labelKey: "group_detail.visibility.public",
    icon: Globe, 
    color: "text-green-500 bg-green-500/10 border-green-500/50",
    tooltipKey: "group_detail.visibility.public_tooltip"
  },
  members_only: { 
    labelKey: "group_detail.visibility.members_only",
    icon: UsersRound, 
    color: "text-blue-500 bg-blue-500/10 border-blue-500/50",
    tooltipKey: "group_detail.visibility.members_only_tooltip"
  },
  closed: { 
    labelKey: "group_detail.visibility.closed",
    icon: Lock, 
    color: "text-red-500 bg-red-500/10 border-red-500/50",
    tooltipKey: "group_detail.visibility.closed_tooltip"
  }
};

/**
 * ClubDetail - Unified group view page
 * Works for:
 * - Visitors (public info only)
 * - Members (public info + member options)
 * - Moderators (full access + management)
 */
export function ClubDetail() {
  const { clubId: clubId } = useParams(); // Route uses :clubId, we use clubId internally
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { setContent: setActionBarContent, clearContent: clearActionBarContent } = useActionBar();
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  
  // React Query mutations
  const joinClubMutation = useJoinClubRequest();
  const leaveClubMutation = useLeaveClub();
  const approveMemberMutation = useApproveMember();
  const rejectMemberMutation = useRejectMember();
  
  // React Query queries
  const { 
    data: club, 
    isLoading: loading, 
    error: clubError,
    refetch: refetchClub 
  } = useClub(clubId, { isAuthenticated });
  
  const { data: gallery = [] } = useClubGallery(clubId, { 
    enabled: !!club && isAuthenticated 
  });
  
  const { data: videos = [] } = useClubVideos(clubId, { 
    enabled: !!club && isAuthenticated 
  });
  
  // Legacy: alias 'club' as 'group' for compatibility with existing code
  const group = club;
  
  // Derived state from club data
  const accessDenied = club?.access_denied || false;
  const accessReason = club?.access_reason || null;
  const isMember = club?.is_member || false;
  const isModerator = club?.is_moderator || false;
  const isCreator = club?.is_creator || false;
  const isPlatformOwner = user?.role === "owner";
  const pendingRequest = club?.pending_request || false;
  const pendingInvite = club?.pending_invite || false;
  
  // Error state
  const error = clubError ? 
    (clubError.response?.data?.detail || 
     (clubError.response?.status === 404 ? t("group_detail.not_found") : t("group_detail.load_error"))) 
    : null;
  
  // Dialog states
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [showAllTrips, setShowAllTrips] = useState(false);

  // Handle invite_accepted param from email action redirect
  useEffect(() => {
    if (searchParams.get("invite_accepted") === "1") {
      toast.success(t("corner.my_groups_edit.invite_accepted_toast"));
      searchParams.delete("invite_accepted");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Set action bar content for moderators/owners (and platform owner for repairs)
  useEffect(() => {
    if ((isModerator || isCreator || isPlatformOwner) && group) {
      setActionBarContent(
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => navigate(`/meus-clubes?tab=list&editgroup=${clubId}`)}
                className="action-bar-btn"
                data-testid="edit-group-btn"
              >
                <Pencil className="h-4 w-4 sm:mr-1" />
                <span className="btn-label">{t("group_detail.btn_edit_group")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("group_detail.tooltip_edit_group")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else {
      clearActionBarContent();
    }
    
    // Cleanup on unmount
    return () => clearActionBarContent();
  }, [isModerator, isCreator, isPlatformOwner, club, clubId, navigate, t, setActionBarContent, clearActionBarContent]);

  // === Member Actions using React Query mutations ===
  const handleRequestJoin = () => {
    if (!isAuthenticated) {
      handleUnauthorizedAccess(navigate, { message: t("group_detail.register_to_join") });
      return;
    }
    
    joinClubMutation.mutate(clubId, {
      onSuccess: () => {
        toast.success(t("group_detail.request_sent"));
        refetchClub(); // Refresh club data to update pending_request state
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || t("group_detail.request_error"));
      }
    });
  };

  const handleLeaveGroup = () => {
    leaveClubMutation.mutate(clubId, {
      onSuccess: () => {
        toast.success(t("group_detail.left_group"));
        navigate("/clubes");
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || t("group_detail.leave_error"));
      }
    });
    setLeaveDialogOpen(false);
  };

  const handleApproveMember = (userId) => {
    approveMemberMutation.mutate({ clubId, userId }, {
      onSuccess: () => {
        toast.success(t("group_detail.member_approved"));
        fetchGroup();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || t("group_detail.approve_error"));
      }
    });
  };

  const handleRejectMember = (userId, reason = "") => {
    rejectMemberMutation.mutate({ clubId, userId, reason }, {
      onSuccess: () => {
        toast.success(t("group_detail.request_rejected"));
        fetchGroup();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || t("group_detail.reject_error"));
      }
    });
  };

  // === Render ===
  if (loading) {
    return (
      <div className="min-h-screen hero-background pt-36 flex flex-col">
        <AppHeader />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen hero-background pt-36 flex flex-col">
        {/* Soft-404 hardening: noindex so Google drops non-existent/removed
            club URLs instead of flagging Soft 404 (2026-07). */}
        <SEO title={t("group_detail.not_found", "Grupo não encontrado")} noindex />
        <AppHeader />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Users className="h-12 w-12 text-muted-foreground" />
          <h1 className="text-lg font-semibold">{error}</h1>
          <Button onClick={() => navigate("/clubes")} data-testid="back-to-clubs-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("group_detail.back_to_groups")}
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const members = club.members || [];
  const pendingMembers = club.pending_requests || [];
  const activeMembers = members.filter(m => m.status === "approved" || m.status === "active" || m.status === "member")
    .sort((a, b) => a.role === "owner" ? -1 : b.role === "owner" ? 1 : 0);
  const trips = club.trips || [];
  
  // Stats with fallback - ensure at least 1 member (the creator)
  const rawStats = club.stats || {};
  const memberCount = rawStats.total_members || activeMembers.length || 1;
  const stats = { 
    total_trips: rawStats.total_trips || trips.length, 
    total_km: rawStats.total_km || 0, 
    total_members: memberCount 
  };
  
  // Get visibility config
  const visibility = club.visibility || "public";
  const visibilityConfig = VISIBILITY_CONFIG[visibility] || VISIBILITY_CONFIG.public;
  const VisibilityIcon = visibilityConfig.icon;

  // Handle access denied view (for closed or members_only groups)
  if (accessDenied) {
    return (
      <TooltipProvider>
      <div className="min-h-screen hero-background pt-36 flex flex-col" data-testid="group-detail-page">
        <SEO
          title={`${getTranslatedValue(club.name, currentLang)} — ${t("seo_dynamic.club_title_suffix")}`}
          description={getTranslatedValue(club.description, currentLang) || getTranslatedValue(club.name, currentLang)}
          path={`/clube/${club.club_id}${club.slug ? `/${club.slug}` : ""}`}
          image={club.logo || club.photo ? `https://mototrips.net/api/uploads/${club.logo || club.photo}` : undefined}
        />
        <AppHeader />
        <main className="w-full max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
          <Card className="bg-card/80 backdrop-blur-sm border-border">
            <CardContent className="flex flex-col items-center justify-center py-16">
              {/* Photo */}
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-card border border-border shadow-xl mb-6">
                {(club.logo || club.photo) ? (
                  <img loading="lazy" 
                    src={getUploadUrl(club.logo || club.photo)}
                    alt={getTranslatedValue(club.name, currentLang)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary/20">
                    <Users className="h-10 w-10 text-primary" />
                  </div>
                )}
              </div>
              
              <h1 className="text-lg sm:text-2xl font-bold mb-2">{getTranslatedValue(club.name, currentLang)}</h1>
              
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className={`mb-4 ${visibilityConfig.color}`}>
                    <VisibilityIcon className="h-3 w-3 mr-1" />
                    {t(visibilityConfig.labelKey)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(visibilityConfig.tooltipKey)}</p>
                </TooltipContent>
              </Tooltip>
              
              {accessReason === "closed" ? (
                <>
                  <Lock className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center mb-6">
                    {t("group_detail.access_denied_closed")}
                  </p>
                </>
              ) : (
                <>
                  <UsersRound className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center mb-2">
                    {t("group_detail.access_denied_members")}
                  </p>
                  {club.member_count > 0 && (
                    <p className="text-sm text-muted-foreground mb-6">
                      {t("group_detail.member_count", { count: club.member_count })}
                    </p>
                  )}
                </>
              )}
              
              {/* Show invite badge if there's a pending invite */}
              {pendingInvite ? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <a href="/clubes" className="inline-block">
                      <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/50 cursor-pointer hover:bg-green-500/20">
                        <Mail className="h-3 w-3 mr-1" />
                        {t("group_detail.invite_received")}
                      </Badge>
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("group_detail.invite_received_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              ) : pendingRequest ? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/50">
                      <Clock className="h-3 w-3 mr-1" />
                      {t("group_detail.pending_request")}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("group_detail.pending_request_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              ) : isAuthenticated ? (
                <Button onClick={handleRequestJoin} disabled={joinClubMutation.isPending} data-testid="request-join-club-btn">
                  {joinClubMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  {t("group_detail.request_join")}
                </Button>
              ) : (
                <Button onClick={() => handleUnauthorizedAccess(navigate, { message: t("group_detail.register_to_join") })} data-testid="login-to-join-club-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("group_detail.login_register")}
                </Button>
              )}
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
    <div className="min-h-screen relative hero-background pt-36 flex flex-col" data-testid="group-detail-page">
      <SEO
        title={`${getTranslatedValue(club.name, currentLang)} — ${t("seo_dynamic.club_title_suffix")}`}
        description={getTranslatedValue(club.description, currentLang) || getTranslatedValue(club.name, currentLang)}
        path={`/clube/${club.club_id}${club.slug ? `/${club.slug}` : ""}`}
        image={club.logo || club.photo ? `https://mototrips.net/api/uploads/${club.logo || club.photo}` : undefined}
      />
      <AppHeader />

      {/* Main Content */}
      <main className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 relative z-10 flex-1`}>
        {/* Group Header */}
        <div className="mb-4" id="topo">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Title Section with Photo */}
            <div className="flex items-start gap-4">
              {/* Photo */}
              <div className="w-16 h-16 rounded-full overflow-hidden bg-card border-4 border-primary/30 shadow-xl flex-shrink-0">
                {(club.logo || club.photo) ? (
                  <img loading="lazy" 
                    src={getUploadUrl(club.logo || club.photo)}
                    alt={getTranslatedValue(club.name, currentLang)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary/20">
                    <Users className="h-7 w-7 text-primary" />
                  </div>
                )}
              </div>

              {/* Name and Location */}
              <div className="flex items-center gap-3">
                <div>
                  <p className="overline mb-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{t("group_detail.group_label")}</p>
                  <h1 className="text-lg sm:text-2xl font-black tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {getTranslatedValue(club.name, currentLang)}
                  </h1>
                  {(club.city || club.region) && (
                    <div className="flex items-center gap-1 text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] mt-1">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>
                        {[club.city, club.region].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Share Button - Circle beside title */}
                <ShareModal 
                  type="club" 
                  data={{ id: clubId, name: getTranslatedValue(club.name, currentLang) }}
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 rounded-full bg-card/60 backdrop-blur-sm shadow-md hover:bg-card border-border"
                  buttonText=""
                  customTrigger={
                    <button className="h-10 w-10 rounded-full bg-card/80 backdrop-blur-sm shadow-md hover:bg-card border border-border flex items-center justify-center transition-colors">
                      <Share2 className="h-4 w-4" />
                    </button>
                  }
                />
              </div>
            </div>

            {/* Stats and Actions Row */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Visibility Badge - only for owner/moderators */}
              {(isModerator || isCreator) && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold shadow-md ${
                      visibility === 'public' 
                        ? 'bg-green-500/90 text-white' 
                        : visibility === 'members_only'
                        ? 'bg-blue-500/90 text-white'
                        : 'bg-zinc-600/90 text-white'
                    }`} data-testid="group-visibility-badge">
                      <VisibilityIcon className="h-3 w-3" />
                      {t(visibilityConfig.labelKey)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t(visibilityConfig.tooltipKey)}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* Role Badge - only for members */}
              {isMember && isCreator && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-orange-500 text-white shadow-md">
                      <Shield className="h-3 w-3" />
                      {t("group_detail.role_primary_contact")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("group_detail.role_primary_contact_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {isMember && isModerator && !isCreator && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-orange-500 text-white shadow-md">
                      <Shield className="h-3 w-3" />
                      {t("group_detail.role_moderator")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("group_detail.role_moderator_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {isMember && !isModerator && !isCreator && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500 text-white shadow-md">
                  <Check className="h-3 w-3" />
                  {t("group_detail.role_member")}
                </span>
              )}
              {pendingInvite && !isMember && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-green-500/90 text-white shadow-md cursor-pointer hover:bg-green-600/90 transition-colors"
                      onClick={() => navigate('/grupos')}
                    >
                      <Mail className="h-3 w-3" />
                      {t("group_detail.invite_received")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("group_detail.invite_click_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {pendingRequest && !pendingInvite && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-yellow-500/90 text-white shadow-md cursor-pointer hover:bg-yellow-600/90 transition-colors"
                      onClick={() => navigate('/grupos')}
                    >
                      <Clock className="h-3 w-3" />
                      {t("group_detail.pending")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("group_detail.pending_click_tooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          
          {/* Description */}
          {getTranslatedValue(club.description, currentLang) && (
            <div className="rounded-lg bg-black/40 backdrop-blur-sm border border-white/10 px-4 py-3 mt-4">
              <p className="text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">{getTranslatedValue(club.description, currentLang)}</p>
            </div>
          )}
          
          {/* Member Action Buttons */}
          {isAuthenticated && !isMember && !pendingRequest && !pendingInvite && (
          <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" onClick={handleRequestJoin} disabled={joinClubMutation.isPending} className="shadow-md" data-testid="request-join-club-hero-btn">
                {joinClubMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-2" />
                )}
                {t("group_detail.request_join")}
              </Button>
          </div>
          )}
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card className="bg-card/80 backdrop-blur-sm border-border">
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-primary/20">
                <Route className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold">{stats.total_trips || trips.length}</p>
                <p className="text-xs text-muted-foreground">
                  {(stats.total_trips || trips.length) === 1 
                    ? t("group_detail.trips_count") 
                    : t("group_detail.trips_count_plural")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm border-border">
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-green-500/20">
                <Gauge className="h-4 w-4 text-green-500" />
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold">{Math.round(stats.total_km || 0).toLocaleString('pt-PT')}</p>
                <p className="text-xs text-muted-foreground">{t("group_detail.km_traveled")}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm border-border">
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-blue-500/20">
                <Users className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold">{stats.total_members || activeMembers.length}</p>
                <p className="text-xs text-muted-foreground">
                  {(stats.total_members || activeMembers.length) === 1 
                    ? t("group_detail.members_count") 
                    : t("group_detail.members_count_plural")}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 backdrop-blur-sm border-border">
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-yellow-500/20">
                <Calendar className="h-4 w-4 text-yellow-500" />
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold">{new Date(club.created_at || Date.now()).getFullYear()}</p>
                <p className="text-xs text-muted-foreground">{t("group_detail.since")}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Trips (left on desktop; below sidebar on mobile) */}
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
            <Card className="bg-card/80 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="h-5 w-5 text-primary" />
                  {t("group_detail.group_trips")}
                  <span className="text-sm text-muted-foreground">({trips.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trips.length === 0 ? (
                  <div className="text-center py-8">
                    <Route className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t("group_detail.no_trips")}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(showAllTrips ? trips : trips.slice(0, 6)).map((trip) => (
                      <TripCard 
                        key={trip.trip_id} 
                        trip={trip}
                        isAuthenticated={isAuthenticated}
                        isOwner={user?.user_id === trip.user_id}
                      />
                    ))}
                  </div>
                )}
                {trips.length > 6 && (
                  <div className="text-center mt-4">
                    <Button variant="outline" onClick={() => setShowAllTrips(prev => !prev)} data-testid="show-all-trips-btn">
                      {showAllTrips ? t("group_detail.show_less_trips", "Mostrar menos") : t("group_detail.show_all_trips", `Ver todos os ${trips.length} passeios`)}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Club Events (public) */}
            <ClubEventsPublicList clubId={clubId} />
          </div>

          {/* Sidebar - Members (right on desktop; on TOP on mobile so logo/members don't end up at the bottom after all trips) */}
          <div className="space-y-6 order-1 lg:order-2">
            {/* Pending Requests Alert Badge - Only for Moderators */}
            {isModerator && pendingMembers.length > 0 && (
              <Card 
                className="bg-card/60 backdrop-blur-sm border-yellow-500/30 cursor-pointer hover:bg-card/70 transition-colors"
                onClick={() => navigate(`/meus-clubes?tab=list&editgroup=${clubId}&edittab=membros`)}
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Pedidos de Adesão Pendentes</p>
                      <p className="text-xs text-muted-foreground">{t("clubs_detail.click_to_manage")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-yellow-500 text-white">{pendingMembers.length}</Badge>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Group Photos Gallery */}
            {(gallery.length > 0 || club.photo) && (
              <GroupPhotoGalleryViewer
                mainPhoto={club.photo}
                gallery={gallery}
                t={t}
              />
            )}

            {/* Active Members - Card view */}
            <Card className="bg-card/80 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5 text-primary" />
                  {t("group_detail.members")}
                  <span className="text-sm text-muted-foreground">({stats.total_members || activeMembers.length || 1})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    {t("group_detail.members_unavailable")}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {activeMembers.map((member) => {
                      const isMainMod = member.role === "owner";
                      const isMod = member.role === "moderator";
                      const moto = member.primary_motorcycle;
                      
                      return (
                        <div 
                          key={member.user_id} 
                          data-testid={`member-row-${member.user_id}`}
                          className="flex items-center p-2.5 bg-muted/30 hover:bg-muted/50 rounded-xl cursor-pointer transition-colors gap-3"
                          onClick={() => navigate(`/meu-canto?motard=${member.user_id}`)}
                        >
                          {/* Avatar */}
                          <Avatar className="h-10 w-10 border-2 border-border flex-shrink-0">
                            <AvatarImage src={member.picture} />
                            <AvatarFallback className="bg-primary/10 text-primary">{member.name?.charAt(0) || "?"}</AvatarFallback>
                          </Avatar>

                          {/* Name + Badge + Moto + Location */}
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm truncate">{member.name}</p>
                            <div className="mt-0.5">
                              {isMainMod && (
                                <Tooltip delayDuration={200}><TooltipTrigger asChild>
                                  <Badge className="bg-yellow-500/90 text-black text-[10px] px-1.5 py-0 cursor-default"><Crown className="h-3 w-3 mr-0.5" />{t("group_detail.main_mod")}</Badge>
                                </TooltipTrigger><TooltipContent>{t("group_detail.owner_tooltip")}</TooltipContent></Tooltip>
                              )}
                              {isMod && !isMainMod && (
                                <Tooltip delayDuration={200}><TooltipTrigger asChild>
                                  <Badge className="bg-blue-500/90 text-white text-[10px] px-1.5 py-0 cursor-default"><Shield className="h-3 w-3 mr-0.5" />{t("group_detail.mod")}</Badge>
                                </TooltipTrigger><TooltipContent>{t("group_detail.mod_tooltip")}</TooltipContent></Tooltip>
                              )}
                              {!isMainMod && !isMod && (
                                <Tooltip delayDuration={200}><TooltipTrigger asChild>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 cursor-default"><Users className="h-3 w-3 mr-0.5" />{t("group_detail.member")}</Badge>
                                </TooltipTrigger><TooltipContent>{t("group_detail.member_tooltip")}</TooltipContent></Tooltip>
                              )}
                            </div>
                            {moto && moto.brand && (
                              <p className="text-xs text-primary/80 truncate mt-0.5">{[moto.brand, moto.model].filter(Boolean).join(" ")}</p>
                            )}
                            {(member.city || member.region) && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{[member.city, member.region].filter(Boolean).join(", ")}</p>
                            )}
                          </div>

                          {/* Motorcycle photo */}
                          {moto?.photo && (
                            <Tooltip delayDuration={300}>
                              <TooltipTrigger asChild>
                                <div className="h-16 w-24 rounded-lg overflow-hidden border border-border flex-shrink-0">
                                  <img loading="lazy" src={getUploadUrl(moto.photo)} alt={[moto.brand, moto.model].filter(Boolean).join(" ")} className="h-full w-full object-cover" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p className="font-medium">{[moto.brand, moto.model].filter(Boolean).join(" ") || t("group_detail.motorcycle")}</p>
                                {getTranslatedValue(moto.nickname, currentLang) && (
                                  <p className="text-xs opacity-80">{quoteNickname(getTranslatedValue(moto.nickname, currentLang))}</p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {/* Arrow */}
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Videos Section - Full width below the cards */}
        {videos.length > 0 && (
          <div className="mt-8">
            <Card className="bg-card/80 backdrop-blur-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Youtube className="h-5 w-5 text-primary" />
                  {t("group_detail.group_videos")}
                  <span className="text-sm text-muted-foreground">({videos.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <YoutubeVideoManager
                  videos={videos}
                  canEdit={false}
                  emptyMessage=""
                  showHeader={false}
                  gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                />
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      <Footer />

      {/* Leave Group Dialog */}
      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("group_detail.leave_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("group_detail.leave_description", { name: getTranslatedValue(club.name, currentLang) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveGroup} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("group_detail.leave_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}

export default ClubDetail;

